import React from 'react';
import MenuButton from './MenuButton';
import { Settings } from 'Components/DesignSystem/Iconography/Icons';
import {
    SelectedComfortableIcon,
    UnSelectedComfortableIcon,
    SelectedNormalIcon,
    UnSelectedNormalIcon,
    SelectedCompactIcon,
    UnSelectedCompactIcon
} from 'assests/GmsIcons';

type SettingsButtonProps = {
    onRowDensityChange: (density: RowDensity) => void;
    isOpen: boolean;
    onToggle: () => void;
    currentDensity?: RowDensity;
}

const SettingsButton = ({ onRowDensityChange, isOpen, onToggle, currentDensity = 'normal' }: SettingsButtonProps) => {

    const handleDensityChange = (density: RowDensity) => {
        onRowDensityChange(density);
        onToggle();
    };

    /* The density glyphs are currentColor (okf/assests 2026-09-08): the selected
       one wears ink, the rest recede to ink-disabled — same pair the old
       #172B4D / #D3D3D3 fills hardcoded. */
    const densityInk = (selected: boolean) => selected ? 'tw-text-ink' : 'tw-text-ink-disabled';

    return (
        <div className="tw-relative">
            <MenuButton
                isOpen={isOpen}
                onToggle={onToggle}
                icon={<Settings />}
                label="Settings"
            />

            {isOpen && (
                <div className="tw-absolute tw-z-10 tw-mt-1 tw-right-0 tw-bg-surface-raised tw-rounded-srf tw-border tw-border-edge tw-shadow-ds-md tw-overflow-hidden tw-min-w-[160px] tw-whitespace-nowrap">
                    <div className="tw-px-2.5 tw-py-2 tw-flex tw-flex-col tw-gap-2.5">

                        <div className="tw-text-ink tw-text-sm">Row Density</div>

                        <div className="tw-flex tw-gap-5">
                            <button data-testid="components-settingsbutton-button-1"
                                onClick={() => handleDensityChange('comfortable')}
                                className={`tw-w-6 tw-h-5 tw-flex tw-items-center tw-justify-center ${densityInk(currentDensity === 'comfortable')}`}
                                aria-label="Comfortable density"
                            >
                                {currentDensity === 'comfortable'
                                    ? <SelectedComfortableIcon />
                                    : <UnSelectedComfortableIcon />
                                }
                            </button>

                            <button data-testid="components-settingsbutton-button-2"
                                onClick={() => handleDensityChange('normal')}
                                className={`tw-w-6 tw-h-5 tw-flex tw-items-center tw-justify-center ${densityInk(currentDensity === 'normal')}`}
                                aria-label="Normal density"
                            >
                                {currentDensity === 'normal'
                                    ? <SelectedNormalIcon />
                                    : <UnSelectedNormalIcon />
                                }
                            </button>

                            <button data-testid="components-settingsbutton-button-3"
                                onClick={() => handleDensityChange('compact')}
                                className={`tw-w-6 tw-h-5 tw-flex tw-items-center tw-justify-center ${densityInk(currentDensity === 'compact')}`}
                                aria-label="Compact density"
                            >
                                {currentDensity === 'compact'
                                    ? <SelectedCompactIcon />
                                    : <UnSelectedCompactIcon />
                                }
                            </button>
                        </div>
                    </div>
                    <div className="tw-w-full tw-h-px tw-bg-edge"></div>
                    <div className="tw-px-2.5 tw-py-2 tw-text-ink tw-text-sm tw-whitespace-nowrap">Column Configuration</div>
                </div>
            )}
        </div>
    );
}

SettingsButton.displayName = 'SettingsButton';

export default SettingsButton;
